import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMoveCard } from './useCards';
import { CardDragItem } from './useDragAndDrop';

/**
 * Optimized drag and drop hook with debouncing and batching
 * Provides instant UI feedback with minimal backend calls
 */
export const useOptimizedCardDrop = (columnId: number) => {
  const queryClient = useQueryClient();
  const moveCard = useMoveCard();
const pendingMoves = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const handleCardDrop = useCallback(
    (item: CardDragItem, targetPosition: number) => {
      // If dropping in the same column at the same position, do nothing
      if (item.columnId === columnId && item.position === targetPosition) {
        return;
      }

      // Clear any pending move for this card
      const existingTimeout = pendingMoves.current.get(item.id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Calculate the actual target position
      let newPosition = targetPosition;
      if (targetPosition === -1) {
        // Drop at the end - get current cards count
        const currentCards = queryClient.getQueryData<any[]>([
          'columns',
          columnId,
          'cards',
        ]);
        newPosition = currentCards ? currentCards.length : 0;
      }

      // Immediately update UI (optimistic update is handled by useMoveCard)
      // Fire the mutation without waiting
      moveCard.mutate(
        {
          id: item.id,
          data: {
            targetColumnId: columnId,
            newPosition,
          },
        },
        {
          onError: (error) => {
            console.error('Failed to move card:', error);
          },
        }
      );
    },
    [columnId, moveCard, queryClient]
  );

  return { handleCardDrop };
};

/**
 * Hook for optimized column position updates
 */
export const useOptimizedColumnDrop = (boardId: number) => {
  const queryClient = useQueryClient();

  const handleColumnDrop = useCallback(
    (columnId: number, targetPosition: number) => {
      // Optimistically update column positions in cache
      queryClient.setQueryData(
        ['boards', boardId, 'columns'],
        (old: any) => {
          if (!Array.isArray(old)) return old;

          const columnIndex = old.findIndex((c: any) => c.id === columnId);
          if (columnIndex === -1) return old;

          const newColumns = [...old];
          const [movedColumn] = newColumns.splice(columnIndex, 1);
          newColumns.splice(targetPosition, 0, movedColumn);

          // Recalculate positions
          return newColumns.map((col: any, index: number) => ({
            ...col,
            position: index,
          }));
        }
      );
    },
    [boardId, queryClient]
  );

  return { handleColumnDrop };
};
